import { useState, useEffect, useRef, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import axiosInstance from "../../API/interceptor";
import TaskViewPanel from "../MyTask/TaskViewPanel";

const PRIO_COLOR = { CRITICAL:"#DC2626",URGENT:"#EA580C",HIGH:"#D97706",MEDIUM:"#059669",LOW:"#6B7280",NORMAL:"#7C3AED" };
const PER_PAGE   = 50;

function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  return `${String(d.getDate()).padStart(2,"0")}-${String(d.getMonth()+1).padStart(2,"0")}-${d.getFullYear()} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
}

const COLS = [
  { key:"unique_seq_id",   label:"ID",        w:80  },
  { key:"task_type",       label:"TASK",      w:200, sticky:"left" },
  { key:"priority",        label:"PRIORITY",  w:100 },
  { key:"task_status",     label:"STATUS",    w:120 },
  { key:"product_name",    label:"WORKSPACE", w:140 },
  { key:"accountable",     label:"ACCOUNT.",  w:140 },
  { key:"responsible",     label:"RESP.",     w:140 },
  { key:"task_created_at", label:"CREATED",   w:140 },
  { key:"deadline",        label:"DEADLINE",  w:140, sticky:"right" },
];

function Pagination({ page, totalPages, totalTasks, perPage, onChange }) {
  if (totalPages <= 1) return null;
  const start = (page - 1) * perPage + 1;
  const end   = Math.min(page * perPage, totalTasks);
  const nums = () => {
    const arr = []; const delta = 2;
    const l = Math.max(2, page - delta); const r = Math.min(totalPages - 1, page + delta);
    arr.push(1); if (l > 2) arr.push("...");
    for (let i = l; i <= r; i++) arr.push(i);
    if (r < totalPages - 1) arr.push("..."); if (totalPages > 1) arr.push(totalPages);
    return arr;
  };
  const Btn = ({ disabled, onClick, children, active }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`min-w-8 h-8 rounded-lg text-xs font-semibold inline-flex items-center justify-center px-2 gap-[3px] border-[1.5px] transition-all duration-150 ${
        active
          ? "border-[#0EA5E9] bg-[#0EA5E9] text-white cursor-pointer"
          : disabled
          ? "border-[#E2E8F0] bg-[#F8FAFC] text-[#CBD5E1] cursor-not-allowed"
          : "border-[#E2E8F0] bg-white text-[#374151] cursor-pointer"
      }`}
    >
      {children}
    </button>
  );
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t-[1.5px] border-[#F1F5F9] bg-[#FAFBFE] flex-wrap gap-2">
      <span className="text-xs text-[#64748B]">
        Showing <strong className="text-[#374151]">{start.toLocaleString()}–{end.toLocaleString()}</strong> of{" "}
        <strong className="text-[#374151]">{totalTasks.toLocaleString()}</strong> tasks
      </span>
      <div className="flex gap-1 items-center flex-wrap">
        <Btn disabled={page===1} onClick={() => onChange(page-1)}><ChevronLeft size={12}/> Prev</Btn>
        {nums().map((p,i) =>
          p==="..."
            ? <span key={`d${i}`} className="text-xs text-[#94A3B8] px-1">…</span>
            : <Btn key={p} active={p===page} onClick={() => onChange(p)}>{p}</Btn>
        )}
        <Btn disabled={page===totalPages} onClick={() => onChange(page+1)}>Next <ChevronRight size={12}/></Btn>
      </div>
    </div>
  );
}

export default function TaskListDetailPanel({ open, onClose, filterBody, extraFilter, title }) {
  const [tasks,        setTasks]        = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [totalTasks,   setTotalTasks]   = useState(0);
  const [totalPages,   setTotalPages]   = useState(1);
  const [page,         setPage]         = useState(1);
  const [taskViewId,   setTaskViewId]   = useState(null);
  const scrollRef = useRef();
  const drag = useRef({ active:false, startX:0, scrollLeft:0 });

  const doFetch = useCallback((pg, isPageChange = false) => {
    if (isPageChange) setTableLoading(true);
    else { setLoading(true); setTasks([]); }

    const body = { ...(filterBody || {}), ...(extraFilter || {}), page: pg, per_page: PER_PAGE };
    axiosInstance.post("/task_assignment/get_task_list_detail", body)
      .then(r => {
        if (r.data?.success) {
          const d = r.data.data;
          setTasks(d.tasks || []);
          setTotalTasks(d.total_tasks || 0);
          setTotalPages(d.total_pages || 1);
        }
      })
      .catch(console.error)
      .finally(() => { setLoading(false); setTableLoading(false); });
  }, [filterBody, extraFilter]);

  useEffect(() => {
    if (!open) { setPage(1); return; }
    setPage(1);
    doFetch(1, false);
  }, [open, filterBody, extraFilter]); // eslint-disable-line

  const handlePageChange = useCallback((newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === page) return;
    setPage(newPage);
    doFetch(newPage, true);
  }, [page, totalPages, doFetch]);

  const onMD = e => { drag.current={active:true,startX:e.pageX-scrollRef.current.offsetLeft,scrollLeft:scrollRef.current.scrollLeft}; scrollRef.current.style.cursor="grabbing"; };
  const onMU = () => { drag.current.active=false; if(scrollRef.current) scrollRef.current.style.cursor="grab"; };
  const onMM = e => { if(!drag.current.active) return; e.preventDefault(); scrollRef.current.scrollLeft=drag.current.scrollLeft-(e.pageX-scrollRef.current.offsetLeft-drag.current.startX); };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} className="fixed inset-0 bg-black/40 z-[1500] backdrop-blur-sm" />

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 bottom-0 w-[min(1297px,96vw)] bg-[#F8FAFC] z-[1501] flex flex-col transition-transform duration-[350ms] ease-[cubic-bezier(.4,0,.2,1)] ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 shrink-0 bg-[linear-gradient(135deg,#0EA5E9,#0284C7)]">
          <div>
            <div className="text-[16px] font-bold text-white">{title || "Task List"}</div>
            <div className="text-[12px] text-white/70 mt-0.5">
              {loading
                ? "Loading..."
                : `${totalTasks.toLocaleString()} task${totalTasks !== 1 ? "s" : ""} found · Page ${page} of ${totalPages}`
              }
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/15 border-none cursor-pointer flex items-center justify-center">
            <X size={16} color="#fff" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center h-[200px] gap-2.5">
              <Loader2 size={22} color="#0EA5E9" className="animate-spin" />
              <span className="text-[13px] text-[#94A3B8]">Loading tasks...</span>
            </div>
          ) : (
            <div className="rounded-xl border border-[#E2E8F0] overflow-hidden bg-white">
              <div ref={scrollRef} className="overflow-x-auto cursor-grab"
                onMouseDown={onMD} onMouseUp={onMU} onMouseLeave={onMU} onMouseMove={onMM}>
                {tableLoading ? (
                  <div className="flex items-center justify-center h-[120px] gap-2">
                    <Loader2 size={18} color="#0EA5E9" className="animate-spin"/>
                    <span className="text-[12px] text-[#94A3B8]">Loading page {page}…</span>
                  </div>
                ) : (
                  <table className="w-full border-separate border-spacing-0 text-[12px]">
                    <thead>
                      <tr>{COLS.map(c => (
                        <th key={c.key}
                          className="px-3 py-[10px] text-left text-[10px] font-bold text-[#94A3B8] tracking-[.8px] uppercase bg-[#F8FAFC] border-b-2 border-[#F1F5F9] whitespace-nowrap"
                          style={{ position:c.sticky?"sticky":"static", left:c.sticky==="left"?0:"auto", right:c.sticky==="right"?0:"auto", zIndex:c.sticky?4:1, minWidth:c.w }}>
                          {c.label}
                        </th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {tasks.length === 0
                        ? <tr><td colSpan={COLS.length} className="py-10 text-center text-[#CBD5E1] text-[13px]">No tasks found</td></tr>
                        : tasks.map((t, i) => {
                            const bg = i%2===0?"#fff":"#FAFBFE";
                            const prio = (t.priority||"").toUpperCase();
                            const ov = t.deadline && t.deadline !== '--' &&
                              new Date(t.deadline.split('-').reverse().join('-').replace(' ','/')) < new Date() &&
                              !["completed","closed"].includes((t.task_status||"").toLowerCase());
                            return (
                              <tr key={t._id||i}
                                onMouseEnter={e=>e.currentTarget.querySelectorAll("td").forEach(td=>td.style.background="#EEF2FF")}
                                onMouseLeave={e=>e.currentTarget.querySelectorAll("td").forEach(td=>td.style.background=bg)}>
                                <td className="px-3 py-[9px] border-b border-[#F1F5F9]" style={{background:bg}}>
                                  <span className="font-mono text-[11px] bg-[#EEF2FF] text-[#4338CA] px-1.5 py-px rounded-[5px] font-semibold">#{t.unique_seq_id||"—"}</span>
                                </td>
                                <td className="px-3 py-[9px] border-b border-[#F1F5F9] sticky left-0 z-[2]" style={{background:bg}}>
                                  <span onClick={()=>setTaskViewId(t._id)}
                                    className="text-[#4F46E5] font-semibold cursor-pointer whitespace-nowrap max-w-[190px] overflow-hidden text-ellipsis block hover:underline">
                                    {t.task_type||"—"}
                                  </span>
                                </td>
                                <td className="px-3 py-[9px] border-b border-[#F1F5F9]" style={{background:bg}}>
                                  <span className="text-[11px] font-bold px-2 py-px rounded-full" style={{background:(PRIO_COLOR[prio]||"#94A3B8")+"18",color:PRIO_COLOR[prio]||"#94A3B8"}}>{prio||"—"}</span>
                                </td>
                                <td className="px-3 py-[9px] border-b border-[#F1F5F9]" style={{background:bg}}>
                                  <span className="text-[11px] px-2 py-px rounded-full bg-[#EEF2FF] text-[#4338CA]">{t.task_status||"—"}</span>
                                </td>
                                <td className="px-3 py-[9px] border-b border-[#F1F5F9]" style={{background:bg}}>
                                  <span className="text-[11px] bg-[#F1F5F9] text-[#475569] px-2 py-px rounded-[6px]">{t.product_name||"—"}</span>
                                </td>
                                <td className="px-3 py-[9px] border-b border-[#F1F5F9] text-[11px] text-[#374151]" style={{background:bg}}>{t.accountable||"—"}</td>
                                <td className="px-3 py-[9px] border-b border-[#F1F5F9] text-[11px] text-[#374151]" style={{background:bg}}>{t.responsible||"—"}</td>
                                <td className="px-3 py-[9px] border-b border-[#F1F5F9] text-[11px] text-[#64748B] whitespace-nowrap" style={{background:bg}}>
                                  {t.created_at || fmtDate(t.task_created_at)}
                                </td>
                                <td className="px-3 py-[9px] border-b border-[#F1F5F9] sticky right-0 z-[2] whitespace-nowrap" style={{background:bg}}>
                                  <span className={`text-[11px] font-semibold ${ov ? "text-[#DC2626]" : "text-[#374151]"}`}>
                                    {t.deadline || "—"}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Pagination */}
              <Pagination
                page={page}
                totalPages={totalPages}
                totalTasks={totalTasks}
                perPage={PER_PAGE}
                onChange={handlePageChange}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-[#F8FAFC] flex items-center justify-between shrink-0 border-t-[1.5px] border-[#F1F5F9]">
          <span className="text-[12px] text-[#94A3B8]">
            {totalTasks > 0 && (
              <><strong className="text-[#374151]">{totalTasks.toLocaleString()}</strong> tasks total · Page <strong className="text-[#374151]">{page}</strong> of <strong className="text-[#374151]">{totalPages}</strong></>
            )}
          </span>
          <button onClick={onClose}
            className="px-[22px] py-2 rounded-[10px] border-0 cursor-pointer text-white text-[12px] font-bold bg-[linear-gradient(135deg,#0EA5E9,#0284C7)]">
            Close
          </button>
        </div>
      </div>

      {/* Task detail drill-through */}
      {taskViewId && <TaskViewPanel taskId={taskViewId} onClose={()=>setTaskViewId(null)}/>}
    </>
  );
}